import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  IconButton,
  Link,
  Text,
  Textarea,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { DetailSheet } from '../../../components/DetailSheet'
import { FileDropzone, useFilePicker } from '../../../components/FileInput'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { LoadingBlock } from '../../../components/Loading'
import { SectionTitle } from '../../../components/SectionCard'
import { StatusBadge } from '../../../components/StatusBadge'
import { Tooltip } from '../../../components/Tooltip'
import { formatDateTime, useAppToast, formatPhone } from '../../../helpers'
import { StudyCreditRequestService } from '../../../services/StudyCreditRequest/StudyCreditRequestService'
import { IStudyCreditRequestDetailRaw, IStudyCreditStudyRaw } from '../../../services/StudyCreditRequest/types'
import {
  ABRA_UNIT_LABELS,
  COURSE_FORMAT_LABELS,
  EDUCATION_FORMAT_LABELS,
  INSTITUTION_LABELS,
  MAX_FILE_SIZE_MB,
  RESPONSIBLE_LABELS,
  STUDY_CREDIT_STATUS
} from './consts'

interface IStudyCreditViewModalProps {
  requestId: number
  isOpen: boolean
  canEdit: boolean
  canDelete: boolean
  onClose: () => void
  onDeleted: () => void
}

/**
 * One study's file list (school records or works) with a discreet "+" button to
 * attach documents the applicant forgot to submit. Rendered only for the file
 * types the study's path actually collects (see StudyBlock).
 */
function StudyFilesSection({
  title,
  type,
  study,
  requestId,
  canEdit,
  onUploaded
}: {
  title: string
  type: 'school_record' | 'work'
  study: IStudyCreditStudyRaw
  requestId: number
  canEdit: boolean
  onUploaded: () => Promise<void>
}) {
  const service = new StudyCreditRequestService()
  const toast = useAppToast()
  const [pending, setPending] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)

  // Este bloco não é um campo de formulário: é um `+` ao lado da legenda, com os arquivos já
  // enviados e os pendentes na mesma lista. Um `<FileDropzone>` aqui seria uma área de soltar
  // dentro de uma ficha de leitura — então ele fica com o layout próprio e pega do hook só o que
  // importa padronizar: o token, a validação e as recusas. É a mesma escolha do `MoneyBlurInput`.
  const { open, inputProps } = useFilePicker({
    accept: 'attachment',
    maxSizeMB: MAX_FILE_SIZE_MB,
    files: pending,
    multiple: true,
    isDisabled: uploading,
    onPick: (accepted) => setPending((prev) => [...prev, ...accepted])
  })

  const existing = study.files.filter((f) => f.type === type)

  // Nothing to show and nothing to add: hide the section entirely.
  if (existing.length === 0 && !canEdit) return null

  const handleUpload = async () => {
    setUploading(true)
    try {
      await service.uploadStudyFiles(requestId, study.id, type, pending)
      setPending([])
      toast.success('Arquivo anexado com sucesso')
      await onUploaded()
    } catch {
      toast.error('Erro ao anexar o arquivo')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Box mt={2}>
      <Flex align="center" gap={1}>
        <Text variant="caption">{title}</Text>
        {canEdit && (
          <>
            <input {...inputProps} />
            <Tooltip label="Adicionar arquivo">
              <IconButton
                aria-label="Adicionar arquivo"
                icon={<Icon name="lni lni-plus" />}
                size="xs"
                variant="ghost"
                colorScheme="magenta"
                onClick={open}
                isDisabled={uploading}
              />
            </Tooltip>
          </>
        )}
      </Flex>

      {existing.length === 0 && pending.length === 0 && <Text variant="caption">Nenhum arquivo enviado.</Text>}
      {existing.map((file) => (
        <Link key={file.id} href={file.file_url} isExternal color="magenta.600" display="block" fontSize="sm">
          <Icon name="lni lni-download" /> {file.original_name}
        </Link>
      ))}

      {pending.length > 0 && (
        <Box mt={1}>
          {pending.map((file, index) => (
            <Flex key={`${file.name}-${file.size}`} align="center" gap={2} mt={1} fontSize="sm">
              <IconButton
                aria-label="Remover arquivo"
                icon={<Icon name="lni lni-close" />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => setPending((prev) => prev.filter((_, i) => i !== index))}
                isDisabled={uploading}
              />
              <Text noOfLines={1}>{file.name}</Text>
            </Flex>
          ))}
          <Button size="xs" colorScheme="magenta" mt={2} onClick={handleUpload} isLoading={uploading}>
            Enviar {pending.length > 1 ? 'arquivos' : 'arquivo'}
          </Button>
        </Box>
      )}
    </Box>
  )
}

function StudyBlock({
  study,
  index,
  requestId,
  canEdit,
  onUploaded
}: {
  study: IStudyCreditStudyRaw
  index: number
  requestId: number
  canEdit: boolean
  onUploaded: () => Promise<void>
}) {
  // Which file types this study's path collects, mirroring the public form
  // (StudyEntryFields): Moema/Brooklin asks for none, extinct units and other
  // institutions ask for both, self-taught asks only for works.
  const isExtinct = study.institution === 'abra' && study.abra_unit === 'extinct_units'
  const isOtherInstitution = study.institution === 'other_institution'
  const isSelfTaught = study.institution === 'self_taught'
  const showSchoolRecord = isExtinct || isOtherInstitution
  const showWork = isExtinct || isOtherInstitution || isSelfTaught

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} mt={2}>
      <Flex align="center" gap={2} mb={2}>
        <Heading as="h4">Estudo {index + 1}</Heading>
        <Badge>{INSTITUTION_LABELS[study.institution]}</Badge>
        {study.abra_unit && <Badge colorScheme="gray">{ABRA_UNIT_LABELS[study.abra_unit]}</Badge>}
      </Flex>
      <DetailSheet
        layout="grid"
        fields={[
          study.course_format && { label: 'Formato do curso', value: COURSE_FORMAT_LABELS[study.course_format] },
          study.course && { label: 'Curso', value: study.course.name },
          study.course_name && { label: 'Curso', value: study.course_name },
          study.institution_name && { label: 'Instituição', value: study.institution_name },
          study.institution === 'abra' && {
            label: 'Ciência confirmada',
            value: study.awareness_confirmed ? 'Sim' : 'Não'
          }
        ]}
      />
      {showSchoolRecord && (
        <StudyFilesSection
          title="Histórico escolar / documentos"
          type="school_record"
          study={study}
          requestId={requestId}
          canEdit={canEdit}
          onUploaded={onUploaded}
        />
      )}
      {showWork && (
        <StudyFilesSection
          title="Trabalhos produzidos"
          type="work"
          study={study}
          requestId={requestId}
          canEdit={canEdit}
          onUploaded={onUploaded}
        />
      )}
    </Box>
  )
}

export function StudyCreditViewModal({
  requestId,
  isOpen,
  canEdit,
  canDelete,
  onClose,
  onDeleted
}: IStudyCreditViewModalProps) {
  const service = new StudyCreditRequestService()
  const toast = useAppToast()
  const deleteDialog = useDisclosure()
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await service.remove(requestId)
      toast.success('Solicitação excluída')
      deleteDialog.onClose()
      onDeleted()
    } catch (error) {
      toast.error('Erro ao excluir a solicitação', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const [request, setRequest] = useState<IStudyCreditRequestDetailRaw | null>(null)
  const [loading, setLoading] = useState(false)
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [resultFiles, setResultFiles] = useState<File[]>([])
  const [uploadingResult, setUploadingResult] = useState(false)

  const loadRequest = async () => {
    setLoading(true)
    try {
      setRequest(await service.get(requestId))
    } catch {
      toast.error('Erro ao carregar a solicitação')
      onClose()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    setRequest(null)
    setNewNote('')
    setResultFiles([])
    loadRequest()
  }, [isOpen, requestId])

  const handleAddNote = async () => {
    if (!newNote.trim()) return
    setSavingNote(true)
    try {
      await service.addNote(requestId, newNote.trim())
      setNewNote('')
      await loadRequest()
    } catch {
      toast.error('Erro ao adicionar a observação')
    } finally {
      setSavingNote(false)
    }
  }

  const handleUploadResult = async () => {
    setUploadingResult(true)
    try {
      await service.uploadResultFiles(requestId, resultFiles)
      setResultFiles([])
      toast.success('Resultado anexado com sucesso')
      await loadRequest()
    } catch {
      toast.error('Erro ao anexar o resultado')
    } finally {
      setUploadingResult(false)
    }
  }

  const handleDeleteResultFile = async (fileId: number) => {
    try {
      await service.deleteResultFile(requestId, fileId)
      await loadRequest()
    } catch {
      toast.error('Erro ao remover o arquivo')
    }
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <Flex align="center" gap={3} pr={8} wrap="wrap">
            <Text>Aproveitamento de Estudos {request?.protocol ?? ''}</Text>
            {request && <StatusBadge status={request.status} statuses={STUDY_CREDIT_STATUS} />}
          </Flex>
        }
        size="wide"
        footer={
          <>
            {canDelete && request && (
              <Button colorScheme="red" variant="outline" mr="auto" onClick={deleteDialog.onOpen}>
                Excluir
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
          </>
        }
      >
        {loading || !request ? (
          <LoadingBlock />
        ) : (
          <>
            <SectionTitle as="h3" mb={2}>
              Dados do aluno
            </SectionTitle>
            <DetailSheet
              layout="grid"
              columns={{ base: 1, md: 3 }}
              fields={[
                { label: 'Nome do(a) Aluno(a)', value: request.student_name },
                { label: 'Responsável', value: RESPONSIBLE_LABELS[request.responsible] },
                request.responsible === 'other' && {
                  label: 'Nome do responsável',
                  value: request.responsible_name
                },
                { label: 'CPF', value: request.cpf },
                { label: 'RG', value: request.rg },
                { label: 'E-mail', value: request.email },
                { label: 'Telefone', value: formatPhone(request.phone) },
                { label: 'Enviado em', value: formatDateTime(request.created_at) }
              ]}
            />

            <Divider my={4} />

            <SectionTitle as="h3" mb={2}>
              Curso desejado
            </SectionTitle>
            <DetailSheet
              layout="grid"
              columns={{ base: 1, md: 3 }}
              fields={[
                { label: 'Formato de ensino', value: EDUCATION_FORMAT_LABELS[request.education_format] },
                { label: 'Curso', value: request.course?.name },
                { label: 'Consultor', value: request.consultant?.name }
              ]}
            />

            <Divider my={4} />

            <SectionTitle as="h3">Estudos Realizados Anteriormente</SectionTitle>
            {request.studies.map((study, index) => (
              <StudyBlock
                key={study.id}
                study={study}
                index={index}
                requestId={requestId}
                canEdit={canEdit}
                onUploaded={loadRequest}
              />
            ))}

            <Divider my={4} />

            <SectionTitle as="h3" mb={2}>
              Resultado
            </SectionTitle>
            {request.result_files.length === 0 && <Text variant="caption">Nenhum resultado anexado ainda.</Text>}
            {request.result_files.map((file) => (
              <Flex key={file.id} align="center" gap={2} mt={1}>
                <Link href={file.file_url} isExternal color="magenta.600" fontSize="sm">
                  <Icon name="lni lni-download" /> {file.original_name}
                </Link>
                <Text variant="caption">
                  {file.uploaded_by?.name ? `por ${file.uploaded_by.name} em ` : ''}
                  {formatDateTime(file.created_at)}
                </Text>
                {canEdit && (
                  <IconButton
                    aria-label="Remover arquivo"
                    icon={<Icon name="lni lni-trash-can" />}
                    size="xs"
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleDeleteResultFile(file.id)}
                  />
                )}
              </Flex>
            ))}
            {canEdit && (
              <>
                <FileDropzone
                  accept="attachment"
                  maxSizeMB={MAX_FILE_SIZE_MB}
                  mt={4}
                  label="Anexar resultado"
                  files={resultFiles}
                  onChange={setResultFiles}
                  isDisabled={uploadingResult}
                />
                {resultFiles.length > 0 && (
                  <Button
                    size="sm"
                    colorScheme="magenta"
                    mt={2}
                    onClick={handleUploadResult}
                    isLoading={uploadingResult}
                  >
                    Enviar {resultFiles.length > 1 ? 'arquivos' : 'arquivo'}
                  </Button>
                )}
              </>
            )}

            <Divider my={4} />

            <Heading as="h3" mb={2}>
              Observações
            </Heading>
            {canEdit && (
              <Flex direction="column" mb={3}>
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  placeholder="Adicionar observação..."
                  disabled={savingNote}
                />
                <Button
                  size="sm"
                  colorScheme="magenta"
                  mt={2}
                  alignSelf="flex-end"
                  onClick={handleAddNote}
                  isLoading={savingNote}
                  isDisabled={!newNote.trim()}
                >
                  Adicionar observação
                </Button>
              </Flex>
            )}
            {request.notes.length === 0 && <Text variant="caption">Nenhuma observação registrada.</Text>}
            {request.notes.map((note) => (
              <Box key={note.id} borderWidth="1px" borderRadius="md" p={3} mt={2} bg="gray.50">
                <Flex align="center" gap={2} mb={1} wrap="wrap">
                  <Text fontSize="xs" fontWeight="bold">
                    {note.created_by?.name ?? 'Sistema'}
                  </Text>
                  <Text variant="caption">{formatDateTime(note.created_at)}</Text>
                  {note.type === 'missing_docs' && <Badge colorScheme="orange">Docs Faltantes</Badge>}
                </Flex>
                <Text fontSize="sm" whiteSpace="pre-wrap">
                  {note.note}
                </Text>
              </Box>
            ))}
          </>
        )}
      </FormModal>
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        onConfirm={handleDelete}
        action="delete"
        entity="solicitação de Aproveitamento de Estudos"
        feminine
        name={request?.protocol}
        isLoading={isDeleting}
      />
    </>
  )
}
